import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  UserGroupIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { fetchTenants, fetchEntraIdentityInventory } from '../api/client';
import Pagination from './Pagination';
import CopyButton from './CopyButton';
import {
  formatDate,
  BoolCell,
  compareVals,
  applyColFilters,
  countActiveFilters,
  useResizableCols,
  TableHead,
} from './coverage/coverageShared';

// ── Tab / collection definitions ───────────────────────────────────────────────
// `collection` is the API query value; columns + filter initializers are per tab.
// `render`: 'bool' → BoolCell, 'date' → formatDate, 'copy' → mono + CopyButton,
//           'num' → numeric, default → plain text.

const USERS_COLS = [
  { key: 'userPrincipalName',          label: 'UPN',            w: 240, align: 'left',   filterType: 'text'    },
  { key: 'displayName',                label: 'Display Name',   w: 190, align: 'left',   filterType: 'text'    },
  { key: 'userType',                   label: 'Type',           w: 100, align: 'left',   filterType: 'select'  },
  { key: 'accountEnabled',             label: 'Enabled',        w:  90, align: 'center', filterType: 'bool'    },
  { key: 'onPremisesSyncEnabled',      label: 'On-prem Synced', w: 120, align: 'center', filterType: 'bool'    },
  { key: 'mail',                       label: 'Mail',           w: 220, align: 'left',   filterType: 'text'    },
  { key: 'createdDateTime',            label: 'Created',        w: 150, align: 'left',   filterType: 'date-op', render: 'date' },
  { key: 'lastPasswordChangeDateTime', label: 'Last Pwd Change', w: 160, align: 'left',  filterType: 'date-op', render: 'date' },
  { key: 'entraObjectId',              label: 'Object ID',      w: 260, align: 'left',   filterType: 'text', render: 'copy' },
  { key: 'collectionDate',             label: 'Snapshot',       w: 120, align: 'left',   filterType: 'date'    },
];

const GROUPS_COLS = [
  { key: 'displayName',        label: 'Display Name',        w: 230, align: 'left',   filterType: 'text'   },
  { key: 'mail',               label: 'Mail',                w: 220, align: 'left',   filterType: 'text'   },
  { key: 'securityEnabled',    label: 'Security',            w:  90, align: 'center', filterType: 'bool'   },
  { key: 'isAssignableToRole', label: 'Role-Assignable',     w: 130, align: 'center', filterType: 'bool'   },
  { key: 'mailEnabled',        label: 'Mail-Enabled',        w: 110, align: 'center', filterType: 'bool'   },
  { key: 'visibility',         label: 'Visibility',          w: 110, align: 'left',   filterType: 'select' },
  { key: 'memberCount',        label: 'Members',             w:  90, align: 'right',  filterType: 'text', render: 'num' },
  { key: 'ownerCount',         label: 'Owners',              w:  85, align: 'right',  filterType: 'text', render: 'num' },
  { key: 'groupTypes',         label: 'Group Types',         w: 130, align: 'left',   filterType: 'text'   },
  { key: 'entraObjectId',      label: 'Object ID',           w: 260, align: 'left',   filterType: 'text', render: 'copy' },
  { key: 'collectionDate',     label: 'Snapshot',            w: 120, align: 'left',   filterType: 'date'   },
];

const ROLES_COLS = [
  { key: 'displayName',    label: 'Role',             w: 230, align: 'left',  filterType: 'text'   },
  { key: 'description',    label: 'Description',      w: 340, align: 'left',  filterType: 'text'   },
  { key: 'memberCount',    label: 'Members',          w:  90, align: 'right', filterType: 'text', render: 'num' },
  { key: 'roleTemplateId', label: 'Role Template ID', w: 260, align: 'left',  filterType: 'text', render: 'copy' },
  { key: 'collectionDate', label: 'Snapshot',         w: 120, align: 'left',  filterType: 'date'   },
];

const ROLE_MEMBERS_COLS = [
  { key: 'roleDisplayName',   label: 'Role',             w: 210, align: 'left', filterType: 'select' },
  { key: 'memberDisplayName', label: 'Member',           w: 200, align: 'left', filterType: 'text'   },
  { key: 'memberUpn',         label: 'Member UPN',       w: 250, align: 'left', filterType: 'text'   },
  { key: 'memberType',        label: 'Member Type',      w: 120, align: 'left', filterType: 'select' },
  { key: 'roleTemplateId',    label: 'Role Template ID', w: 260, align: 'left', filterType: 'text', render: 'copy' },
  { key: 'memberId',          label: 'Member ID',        w: 260, align: 'left', filterType: 'text', render: 'copy' },
  { key: 'collectionDate',    label: 'Snapshot',         w: 120, align: 'left', filterType: 'date'   },
];

const TABS = [
  { id: 'users',        label: 'Users',           cols: USERS_COLS },
  { id: 'groups',       label: 'Groups',          cols: GROUPS_COLS },
  { id: 'roles',        label: 'Directory Roles', cols: ROLES_COLS },
  { id: 'role-members', label: 'Role Members',    cols: ROLE_MEMBERS_COLS },
];

const COLS_BY_TAB = {
  'users': USERS_COLS, 'groups': GROUPS_COLS, 'roles': ROLES_COLS, 'role-members': ROLE_MEMBERS_COLS,
};

// Build an empty filter object for a column set (date-op cols get a companion `_op`).
function initFilters(cols) {
  const f = {};
  for (const c of cols) {
    f[c.key] = '';
    if (c.filterType === 'date-op') f[`${c.key}_op`] = '>=';
  }
  return f;
}

const today = () => new Date().toISOString().slice(0, 10);

// ── Main component ──────────────────────────────────────────────────────────────

export default function EntraIdentityPage() {
  const [tenants, setTenants]               = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [snapshotDate, setSnapshotDate]     = useState(() => today());

  const [activeTab, setActiveTab] = useState('users');

  // data is { users:[], groups:[], roles:[], 'role-members':[] } once loaded, else null.
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [loadTime, setLoadTime]     = useState(null);
  const [loadedDate, setLoadedDate] = useState(null);   // the date the loaded data actually came from

  // Per-tab filter state (keyed by tab id)
  const [filtersByTab, setFiltersByTab] = useState({
    'users': initFilters(USERS_COLS),
    'groups': initFilters(GROUPS_COLS),
    'roles': initFilters(ROLES_COLS),
    'role-members': initFilters(ROLE_MEMBERS_COLS),
  });

  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir]     = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize]       = useState(10);

  // Load tenants on mount (no data auto-fetch — user clicks Load)
  useEffect(() => {
    fetchTenants()
      .then((d) => {
        const active = (Array.isArray(d) ? d : []).filter((t) => t.Status !== 'inactive');
        setTenants(active);
      })
      .catch(() => {})
      .finally(() => setTenantsLoading(false));
  }, []);

  // Reset sort/page when switching tabs
  useEffect(() => {
    setSortField('');
    setSortDir('asc');
    setCurrentPage(1);
  }, [activeTab]);

  // Fetch all four collections for a given date.
  const fetchAll = useCallback(async (date) => {
    const opts = date ? { snapshotDate: date } : {};
    const [users, groups, roles, roleMembers] = await Promise.all(
      ['users', 'groups', 'roles', 'role-members'].map((c) =>
        fetchEntraIdentityInventory(selectedTenant, { ...opts, collection: c })
          .then((r) => (Array.isArray(r) ? r : []))
      )
    );
    // Resolve each role member's friendly role name from the roles snapshot
    // (role-member edges only carry role_template_id, not the role's display name).
    const roleNameById = {};
    for (const r of roles) {
      if (r.roleTemplateId) roleNameById[r.roleTemplateId.toLowerCase()] = r.displayName;
    }
    const roleMembersEnriched = roleMembers.map((m) => ({
      ...m,
      roleDisplayName: (m.roleTemplateId && roleNameById[m.roleTemplateId.toLowerCase()]) || m.roleTemplateId || '',
    }));
    return { 'users': users, 'groups': groups, 'roles': roles, 'role-members': roleMembersEnriched };
  }, [selectedTenant]);

  const totalRows = (d) => (d ? Object.values(d).reduce((a, arr) => a + arr.length, 0) : 0);

  const handleLoad = useCallback(async () => {
    if (!selectedTenant) return;
    setLoading(true);
    setError(null);
    setData(null);
    setLoadTime(null);
    setFiltersByTab({
      'users': initFilters(USERS_COLS),
      'groups': initFilters(GROUPS_COLS),
      'roles': initFilters(ROLES_COLS),
      'role-members': initFilters(ROLE_MEMBERS_COLS),
    });
    setSortField('');
    setSortDir('asc');
    setCurrentPage(1);

    const t0 = performance.now();
    const td = today();
    const requested = snapshotDate || td;
    try {
      let res = await fetchAll(requested);
      let effectiveDate = requested;
      // If the requested date is today and nothing landed, the daily snapshot may not
      // have run yet — fall back to yesterday (ATS keeps the last 2 collection_dates).
      if (totalRows(res) === 0 && requested === td) {
        const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
        const res2 = await fetchAll(yesterday);
        if (totalRows(res2) > 0) {
          res = res2;
          effectiveDate = yesterday;
          setSnapshotDate(yesterday);
        }
      }
      setData(res);
      setLoadedDate(effectiveDate);
      setLoadTime(((performance.now() - t0) / 1000).toFixed(1));
    } catch (err) {
      setError(err?.message || 'Failed to load identity inventory.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, snapshotDate, fetchAll]);

  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setCurrentPage(1);
  }, [sortField]);

  const setFilter = useCallback((key, value) => {
    setFiltersByTab((p) => ({ ...p, [activeTab]: { ...p[activeTab], [key]: value } }));
    setCurrentPage(1);
  }, [activeTab]);

  const clearFilters = useCallback(() => {
    setFiltersByTab((p) => ({ ...p, [activeTab]: initFilters(COLS_BY_TAB[activeTab]) }));
    setCurrentPage(1);
  }, [activeTab]);

  const activeCols    = COLS_BY_TAB[activeTab];
  const activeRows    = data ? data[activeTab] : null;
  const activeFilters = filtersByTab[activeTab];

  // Distinct values for select-type column filters on the active tab
  const distinct = useMemo(() => {
    const out = {};
    for (const col of activeCols) {
      if (col.filterType === 'select' || col.filterType === 'multi-select') {
        out[col.key] = [...new Set((activeRows || []).map((r) => r[col.key]).filter(Boolean))].sort();
      }
    }
    return out;
  }, [activeCols, activeRows]);

  // Filter → sort → paginate
  const filtered = useMemo(() => {
    if (!activeRows) return [];
    let rows = applyColFilters(activeRows, activeCols, activeFilters);
    if (sortField) rows = [...rows].sort((a, b) => compareVals(a[sortField], b[sortField], sortDir));
    return rows;
  }, [activeRows, activeCols, activeFilters, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const activeFilterCount = countActiveFilters(activeFilters);

  function tabLabel(tab) {
    const n = data ? data[tab.id].length : null;
    return n === null ? tab.label : `${tab.label} (${n.toLocaleString()})`;
  }

  return (
    <div className="p-6 space-y-6">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div>
        <div className="text-xs text-gray-500 mb-1">Coverage Analysis</div>
        <h1 className="text-xl font-semibold text-gray-100 flex items-center gap-2">
          <UserGroupIcon className="h-5 w-5 text-blue-400" />
          Identity Inventory
        </h1>
        <p className="text-xs text-gray-500 mt-1">
          Daily snapshot of Entra users, groups, directory roles, and role holders (read-only).
        </p>
      </div>

      {/* ── Filter bar ──────────────────────────────────────────────────────── */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Tenant</label>
            <select
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
              disabled={tenantsLoading}
              className="input-field py-1 px-2 text-xs min-w-[200px]"
            >
              <option value="">Select a tenant…</option>
              {tenants
                .map((t) => {
                  const label = t.ShortName && t.FriendlyName
                    ? `(${t.ShortName}) ${t.FriendlyName}`
                    : (t.ShortName || t.FriendlyName || t.Domain || t.TenantID);
                  return { id: t.TenantID, label };
                })
                .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
                .map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Snapshot Date</label>
            <input
              type="date"
              value={snapshotDate}
              onChange={(e) => setSnapshotDate(e.target.value)}
              className="input-field py-1 px-2 text-xs w-36"
            />
          </div>

          <button
            type="button"
            onClick={handleLoad}
            disabled={loading || !selectedTenant}
            className="btn-primary text-xs px-3 py-1.5"
            title={!selectedTenant ? 'Select a tenant first' : undefined}
          >
            {loading ? 'Loading…' : 'Load'}
          </button>

          {loadTime && !loading && (
            <span className="text-[11px] text-gray-600">
              Loaded {totalRows(data).toLocaleString()} rows in <span className="text-gray-500">{loadTime}s</span>
              {loadedDate && <span className="text-gray-600"> · snapshot {loadedDate}</span>}
            </span>
          )}
        </div>
      </div>

      {/* ── Error banner ────────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-3 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-sm text-red-300">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-400" />
          <span className="flex-1">{error}</span>
          <button onClick={handleLoad} className="text-xs underline hover:no-underline">Retry</button>
        </div>
      )}

      {/* ── Results ─────────────────────────────────────────────────────────── */}
      {(data !== null || loading) && (
        <div className="card overflow-hidden">
          {/* Tabs */}
          <div className="flex items-center gap-1.5 px-4 pt-3 pb-2 border-b border-gray-800 flex-wrap">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-600/20 text-blue-400 border-blue-500/50'
                    : 'border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                {tabLabel(tab)}
              </button>
            ))}
          </div>

          {/* Groups membership note */}
          {activeTab === 'groups' && (
            <div className="mx-4 my-2 flex items-start gap-2 p-2.5 bg-gray-800/40 border border-gray-700/50 rounded text-[11px] text-gray-400">
              <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-500 mt-0.5" />
              <span>Group <span className="text-gray-300">membership</span> (which users belong to a group) is not in this snapshot — it lives in Databricks only.</span>
            </div>
          )}

          {/* Toolbar */}
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-800 min-h-[40px]">
            {activeRows !== null && !loading && (
              <span className="text-xs text-gray-500">
                {filtered.length.toLocaleString()} {filtered.length === 1 ? 'row' : 'rows'}
                {activeFilterCount > 0 && activeRows.length !== filtered.length
                  ? ` (filtered from ${activeRows.length.toLocaleString()})`
                  : ''}
              </span>
            )}
            {loading && (
              <span className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className="animate-spin inline-block h-3 w-3 border-b border-blue-500 rounded-full" />
                Loading…
              </span>
            )}
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 border border-gray-700 hover:border-gray-600 rounded px-2 py-0.5 transition-colors"
              >
                <XMarkIcon className="h-3 w-3" />
                Clear {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}
              </button>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <IdentityTable
              key={activeTab}
              cols={activeCols}
              rows={paged}
              loading={loading}
              sortField={sortField} sortDir={sortDir} onSort={handleSort}
              filters={activeFilters} onFilterChange={setFilter} distinct={distinct}
            />
          </div>

          {/* Pagination */}
          {!loading && filtered.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filtered.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── Generic identity table ──────────────────────────────────────────────────────

function renderCell(col, row) {
  const v = row[col.key];
  if (col.filterType === 'bool') return <BoolCell value={v} />;
  if (col.render === 'date') return <span className="text-gray-400">{formatDate(v)}</span>;
  if (col.render === 'num') return <span className="text-gray-300">{v == null ? '—' : Number(v).toLocaleString()}</span>;
  if (col.render === 'copy') {
    return v
      ? <span className="font-mono text-gray-400 text-[11px]">{v}<CopyButton text={v} /></span>
      : <span className="text-gray-600">—</span>;
  }
  return <span className="text-gray-300">{v || '—'}</span>;
}

function IdentityTable({ cols, rows, loading, sortField, sortDir, onSort, filters, onFilterChange, distinct }) {
  const { colWidths, thRefs, tableRef, startResize } = useResizableCols(cols.map((c) => c.w));
  return (
    <table
      ref={tableRef}
      className="text-xs text-gray-300"
      style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
    >
      <TableHead
        cols={cols} colWidths={colWidths} thRefs={thRefs} startResize={startResize}
        sortField={sortField} sortDir={sortDir} onSort={onSort}
        filters={filters} onFilterChange={onFilterChange} distinct={distinct}
      />
      <tbody>
        {loading ? (
          <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-gray-500">
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />Loading…
            </div>
          </td></tr>
        ) : rows.length === 0 ? (
          <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-gray-500">
            No rows for this snapshot. If the tenant is active, the daily snapshot may not have landed for the selected date.
          </td></tr>
        ) : rows.map((row, idx) => (
          // Role-member rows have no entraObjectId and a member can hold several
          // roles (memberId repeats), so key on the unique (role, member) composite
          // to avoid duplicate React keys that would break reorder-on-sort.
          <tr key={row.entraObjectId || `${row.roleTemplateId || ''}|${row.memberId || ''}` || idx} className="group border-b border-gray-800/50 hover:bg-gray-800/30">
            {cols.map((col) => (
              <td
                key={col.key}
                className={`px-3 py-2 truncate ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'}`}
                title={col.render === 'bool' ? undefined : (row[col.key] != null ? String(row[col.key]) : undefined)}
              >
                {renderCell(col, row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
